import { useId, useState } from 'react';
import { IconButton } from '@eventbrite/marmalade';
import Image from 'next/image';
import { defineMessages, useIntl } from 'react-intl';

import tooltipPreview from '../../../../public/images/featured-event-tooltip-preview.png';

import styles from './FeaturedEvent.module.scss';

const messages = defineMessages({
  pin: {
    id: 'Pin {eventName} as the highlighted event',
    defaultMessage: 'Pin {eventName} as the highlighted event',
  },
  unpin: {
    id: 'Remove {eventName} from highlighted events',
    defaultMessage: 'Remove {eventName} from highlighted events',
  },
  tooltipTitle: {
    id: 'Pin this event',
    defaultMessage: 'Pin this event',
  },
  tooltipDescription: {
    id: "It'll appear first and highlighted on your profile. You can pin one event at a time.",
    defaultMessage:
      "It'll appear first and highlighted on your profile. You can pin one event at a time.",
  },
});

interface Props {
  eventName: string;
  isPinned: boolean;
  showTooltip?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

function FeaturedEventPinGlyph({ isPinned }: { isPinned: boolean }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      data-icon={isPinned ? 'unpin' : 'pin'}
      xmlns="http://www.w3.org/2000/svg"
    >
      {isPinned ? (
        <>
          <path
            d="M16.0752 15.7217C15.801 16.1204 15.595 16.5642 15.4688 17.0352L13.8711 22.999L8.17773 17.3057L3.0166 22.4678L1.5332 20.9844L6.69434 15.8232L1 10.1299L6.96484 8.53223C7.43579 8.40593 7.87851 8.1981 8.27734 7.92383L16.0752 15.7217Z"
            fill="#161719"
          />
          <path
            d="M23.001 11.2432L17.457 14.4443C17.3445 14.5093 17.2359 14.5805 17.1299 14.6553L9.34473 6.86914C9.41919 6.76353 9.49096 6.65597 9.55566 6.54395L12.7578 1L23.001 11.2432Z"
            fill="#161719"
          />
          <path
            d="M0.66996 3.14481L2.08417 1.7306L21.8832 21.5296L20.469 22.9438L0.66996 3.14481Z"
            fill="#161719"
          />
        </>
      ) : (
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12.7573 1L23.0005 11.2432L17.4565 14.4443C17.1337 14.6308 16.8363 14.8585 16.5728 15.1221C16.0431 15.6517 15.6622 16.3117 15.4683 17.0352L13.8706 22.999L8.17725 17.3057L3.01611 22.4678L1.53271 20.9844L6.69385 15.8232L1.00049 10.1299L6.96436 8.53223C7.6878 8.33831 8.34778 7.9573 8.87744 7.42773C9.14101 7.16417 9.36874 6.86671 9.55518 6.54395L12.7573 1Z"
          fill="#161719"
        />
      )}
    </svg>
  );
}

export function FeaturedEventPinButton({
  eventName,
  isPinned,
  showTooltip = false,
  disabled,
  onClick,
}: Props) {
  const intl = useIntl();
  const tooltipId = useId();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const label = intl.formatMessage(isPinned ? messages.unpin : messages.pin, { eventName });
  const shouldRenderTooltip = showTooltip && !isPinned && isTooltipOpen;

  return (
    <span
      className={styles.pinTooltipAnchor}
      onMouseEnter={() => setIsTooltipOpen(true)}
      onMouseLeave={() => setIsTooltipOpen(false)}
      onFocusCapture={() => setIsTooltipOpen(true)}
      onBlurCapture={() => setIsTooltipOpen(false)}
    >
      <IconButton
        icon={<FeaturedEventPinGlyph isPinned={isPinned} />}
        outline
        disabled={disabled}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsTooltipOpen(false);
          onClick();
        }}
        aria-label={label}
        aria-describedby={shouldRenderTooltip ? tooltipId : undefined}
        aria-pressed={isPinned}
        className={isPinned ? styles.pinnedButton : styles.pinButton}
        data-testid={`featured-event-pin-${isPinned ? 'active' : 'inactive'}`}
      />
      {shouldRenderTooltip ? (
        <span id={tooltipId} role="tooltip" className={styles.pinTooltip}>
          <Image
            src={tooltipPreview}
            alt=""
            width={256}
            height={128}
            aria-hidden
            className={styles.pinTooltipPreview}
            unoptimized
          />
          <span className={styles.pinTooltipCopy}>
            <span className={styles.pinTooltipTitle}>
              {intl.formatMessage(messages.tooltipTitle)}
            </span>
            <span className={styles.pinTooltipDescription}>
              {intl.formatMessage(messages.tooltipDescription)}
            </span>
          </span>
        </span>
      ) : null}
    </span>
  );
}
